// Pure detection of contradictory (mutually exclusive) facts on a property's
// factsheet. Framework-agnostic: operates only on the `state.factsheet.facts`
// shape ({ [factDefId]: Instance[] }) and a set of parsed rules. It intentionally
// does NOT read the blueprint, so ids that aren't in the loaded blueprint simply
// never match rather than throwing.
//
// This module must not import the redux store or notificationService.

/**
 * Parses a spec token into a structural matcher.
 *
 * Token forms (letters are spec notation, stripped here):
 *   'f269'          -> { factId: '269' }                                  plain fact presence
 *   'f65:a608'      -> { factId: '65', attrId: '608' }                    attribute presence
 *   'f708:a68.value'-> { factId: '708', attrId: '68', valueCheck: true }  attribute has a value
 *
 * @param {string} token
 * @returns {{ factId: string, attrId?: string, valueCheck: boolean, raw: string }}
 */
export const parseToken = (token) => {
  if (token == null || token === '') {
    return { factId: undefined, attrId: undefined, valueCheck: false, raw: token }
  }

  let body = token
  let valueCheck = false
  if (body.endsWith('.value')) {
    valueCheck = true
    body = body.slice(0, -'.value'.length)
  }

  const parts = body.split(':')
  let factId
  let attrId
  if (parts.length === 1) {
    // 'f269' / '269'
    factId = parts[0].replace(/^f/, '')
  } else if (parts[1].startsWith('a')) {
    // 'f65:a608'
    factId = parts[0].replace(/^f/, '')
    attrId = parts[1].slice(1)
  }

  return { factId, attrId, valueCheck, raw: token }
}

/**
 * Whether the property's facts satisfy a single parsed token.
 * @param {Object} facts state.factsheet.facts
 * @param {{ factId: string, attrId?: string, valueCheck?: boolean }} parsed
 * @returns {boolean}
 */
export const matchToken = (facts, parsed) => {
  const instances = facts?.[parsed.factId]
  if (!instances || instances.length === 0) {
    return false
  }
  if (!parsed.attrId) {
    return true // plain fact presence
  }
  if (parsed.valueCheck) {
    return instances.some((instance) => {
      const value = instance.attributes?.[parsed.attrId]?.value
      return value != null && value !== ''
    })
  }
  return instances.some((instance) => instance.attributes?.[parsed.attrId] !== undefined)
}

/**
 * Detects contradictions across all rules. A rule is violated when at least one
 * token in each of its two groups is present.
 *
 * - `conflictingTokens` is the union of present tokens from both groups of every
 *   violated rule.
 * - `count` is the number of distinct such tokens (facts involved in a conflict).
 * - `pairs` is the cross product of present groupB × groupA tokens per violated
 *   rule (each `{ ruleId, left, right }` is one conflicting fact pair, `left`
 *   being the groupB side), used to render the detail view.
 *
 * @param {Object} facts state.factsheet.facts
 * @param {Array<{ id: string, groupA: Array, groupB: Array }>} rules parsed rules
 * @returns {{ count: number, conflictingTokens: string[], pairs: Array<{ ruleId: string, left: string, right: string }> }}
 */
export const detectContradictions = (facts, rules) => {
  const safeFacts = facts || {}
  const conflictingTokens = new Set()
  const pairs = [];

  (rules || []).forEach((rule) => {
    const presentA = rule.groupA.filter((parsed) => matchToken(safeFacts, parsed))
    const presentB = rule.groupB.filter((parsed) => matchToken(safeFacts, parsed))
    if (presentA.length > 0 && presentB.length > 0) {
      presentA.forEach((parsed) => conflictingTokens.add(parsed.raw))
      presentB.forEach((parsed) => conflictingTokens.add(parsed.raw))
      presentB.forEach((b) => presentA.forEach((a) => {
        pairs.push({ ruleId: rule.id, left: b.raw, right: a.raw })
      }))
    }
  })

  return { count: conflictingTokens.size, conflictingTokens: [...conflictingTokens], pairs }
}

// stable identity of a conflicting pair, used to diff two detection runs.
const pairKey = (pair) => `${pair.ruleId}|${pair.left}|${pair.right}`

/**
 * Detects the conflicting pairs that a single fact/attribute change *introduces* —
 * i.e. pairs present after the change that weren't present before it. Used to
 * decide whether such a change should raise the fact-conflict modal, and to
 * label which side is the freshly added fact.
 *
 * The just-added token is identified by `addedFactId` (and `addedAttrId`, when an
 * attribute was set): whichever side of a new pair matches it is the `added`
 * side; the other side is the `existing` conflicting fact.
 *
 * @param {Object} prevFacts state.factsheet.facts before the set
 * @param {Object} nextFacts state.factsheet.facts after the set
 * @param {string} addedFactId fact def-id of the set fact/attribute
 * @param {string} [addedAttrId] attribute def-id, when an attribute was set
 * @param {Array} rules parsed contradiction rules
 * @returns {{ pairs: Array<{ existing: string, added: string }> }|null}
 *   the introduced conflicts (each pair's `added` names the set fact), or null
 *   when the set introduces none
 */
export const detectNewConflicts = (prevFacts, nextFacts, addedFactId, addedAttrId, rules) => {
  const prevKeys = new Set(detectContradictions(prevFacts, rules).pairs.map(pairKey))
  const nextPairs = detectContradictions(nextFacts, rules).pairs

  const isAdded = (token) => {
    const parsed = parseToken(token)
    if (parsed.factId !== String(addedFactId)) {
      return false
    }
    // setting an attribute only matches tokens that name that same attribute;
    // setting a plain fact matches the fact-level token.
    return addedAttrId == null || parsed.attrId === String(addedAttrId)
  }

  const pairs = []
  nextPairs.forEach((pair) => {
    if (prevKeys.has(pairKey(pair))) {
      // already existed before this set
      return
    }
    if (isAdded(pair.left)) {
      pairs.push({ existing: pair.right, added: pair.left })
    } else if (isAdded(pair.right)) {
      pairs.push({ existing: pair.left, added: pair.right })
    }
    // new pairs not involving the set token are ignored: this set can only
    // be responsible for pairs it participates in.
  })

  if (pairs.length === 0) {
    return null
  }
  return { pairs }
}
